const RA_STORE = "RaStore";
// sessionStorage isn't available in some contexts. We need to detect it
const testSessionStorage = () => {
  // eslint-disable-next-line eqeqeq
  if (typeof window === "undefined" || window.sessionStorage == undefined) {
    return false;
  }
  try {
    window.sessionStorage.setItem("test", "test");
    window.sessionStorage.removeItem("test");
    return true;
  } catch (e) {
    return false;
  }
};
let sessionStorageAvailable = testSessionStorage();
/**
 * Store using sessionStorage, or memory storage if sessionStorage is not available
 *
 * @example
 *
 * import { sessionStorageStore } from 'react-admin';
 *
 * const App = () => (
 *    <Admin store={sessionStorageStore()}>
 *       ...
 *   </Admin>
 * );
 */
export const sessionStorageStore = (version = "1", appKey = "") => {
  const prefix = `${RA_STORE}${appKey}`;
  const prefixLength = prefix.length;
  const subscriptions = {};
  const publish = (key, value) => {
    Object.keys(subscriptions).forEach((id) => {
      if (!subscriptions[id]) return; // may happen if a component unmounts after a first subscriber was notified
      if (subscriptions[id].key === key) {
        subscriptions[id].callback(value);
      }
    });
  };
  // Note: Unlike localStorage, 'storage' event doesn't fire for sessionStorage changes across tabs/windows.
  // So, there's no need for an onSessionStorageChange handler or event listener.
  return {
    setup: () => {
      if (sessionStorageAvailable) {
        const storedVersion = getStorage().getItem(`${prefix}.version`);
        if (storedVersion && storedVersion !== version) {
          const storage = getStorage();
          Object.keys(storage).forEach((key) => {
            if (key.startsWith(prefix)) {
              storage.removeItem(key);
            }
          });
        }
        getStorage().setItem(`${prefix}.version`, version);
      }
    },
    teardown: () => {
      // No event listener to remove for sessionStorage
    },
    getItem(key, defaultValue) {
      const valueFromStorage = getStorage().getItem(`${prefix}.${key}`);
      // eslint-disable-next-line eqeqeq
      return valueFromStorage == null
        ? //@ts-ignore
          defaultValue
        : tryParse(valueFromStorage);
    },
    setItem(key, value) {
      if (value === undefined) {
        getStorage().removeItem(`${prefix}.${key}`);
      } else {
        getStorage().setItem(`${prefix}.${key}`, JSON.stringify(value));
      }
      publish(key, value);
    },
    removeItem(key) {
      getStorage().removeItem(`${prefix}.${key}`);
      publish(key, undefined);
    },
    removeItems(keyPrefix) {
      const storage = getStorage();
      const keysToRemove = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && key.startsWith(`${prefix}.${keyPrefix}`)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => {
        storage.removeItem(key);
        const publishKey = key.substring(prefixLength + 1);
        publish(publishKey, undefined);
      });
    },
    reset() {
      const storage = getStorage();
      const keysToRemove = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && key.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => {
        storage.removeItem(key);
        const publishKey = key.substring(prefixLength + 1);
        publish(publishKey, undefined);
      });
    },
    subscribe: (key, callback) => {
      const id = Math.random().toString();
      subscriptions[id] = {
        key,
        callback,
      };
      return () => {
        delete subscriptions[id];
      };
    },
  };
};
const tryParse = (value) => {
  try {
    return JSON.parse(value);
  } catch (e) {
    return value;
  }
};
class SessionStorageShim {
  valuesMap = new Map();
  getItem(key) {
    if (this.valuesMap.has(key)) {
      return String(this.valuesMap.get(key));
    }
    return null;
  }
  setItem(key, value) {
    this.valuesMap.set(key, value);
  }
  removeItem(key) {
    this.valuesMap.delete(key);
  }
  clear() {
    this.valuesMap.clear();
  }
  key(i) {
    if (i < 0 || i >= this.valuesMap.size) {
      return null;
    }
    const arr = Array.from(this.valuesMap.keys());
    return arr[i];
  }
  get length() {
    return this.valuesMap.size;
  }
}
const memoryStorage = new SessionStorageShim();
export const getStorage = () => {
  return sessionStorageAvailable ? window.sessionStorage : memoryStorage;
};
export default sessionStorageStore;
